import hyperdom from 'hyperdom'
import * as gtmDataLayer from '../../../analytics/gtm/dataLayer.js'
import scroller from '../../../scroller.js'
import formatPrice from '../../../packages/ui-helpers/formatPrice.js'
import PassengersTabSection from './passengersTabSection.jsx'
import FrequentTravellerDiscount from './frequentTravellerDiscount.jsx'
import PastGuestBenefit from './pastGuestBenefit.jsx'
import TravelProtectionPlans from './travelProtectionPlans.jsx'
import PrepaidGratuities from './prepaidGratuities.jsx'
import SpecialRequirements from './specialRequirements.jsx'
import DiscountCode from '../../discountCode.jsx'
import breakpoints from '../../../packages/ui-helpers/breakpoints.js'
import {scrollToAndFocus} from '../../../../booking-engine/components/materialDesign/scrollToAndFocus.js'

export default class PassengersTab {
  constructor ({
    browserCapabilities,
    config,
    departure,
    features,
    passengersBooking,
    priceBreakdown,
    serverErrorsPanel,
    user,
    quoteCriteria,
    modals,
    promotions,
    bookingCreated,
    progress,
    hideLoading,
    clearErrors,
    showLoading,
    api,
  }) {
    this.config = config
    this.user = user
    this.departure = departure
    this.quoteCriteria = quoteCriteria
    this.browserCapabilities = browserCapabilities

    this.features = features
    this.serverErrorsPanel = serverErrorsPanel
    this.priceBreakdown = priceBreakdown
    this.passengersBooking = passengersBooking
    this.departureCode = this.departure.code

    this.discountCode = new DiscountCode({
      config,
      quoteCriteria,
      showLoading,
      modals,
      promotions,
      bookingCreated,
      progress,
      hideLoading,
      clearErrors,
      departure,
      api,
      user,
    })

    this.prepaidGratuities = new PrepaidGratuities({
      config,
      quoteCriteria,
    })
    this.specialRequirements = new SpecialRequirements({
      browserCapabilities,
      config,
      quoteCriteria,
    })
    this.frequentTravellerDiscount = new FrequentTravellerDiscount({
      config,
      departure,
      quoteCriteria,
    })
    this.pastGuestBenefit = new PastGuestBenefit({
      config,
      quoteCriteria,
      user,
    })
    this.travelProtectionPlans = this.config.insurance ? new TravelProtectionPlans({
      config,
      features,
      quoteCriteria,
    }) : undefined
    this.discountsAndExtrasSection = this.createExtrasSection()
    this.specialRequirementsSection = this.createSpecialRequirementsSection()
  }

  onadd () {
    gtmDataLayer.activateOptimize()
  }

  onupdate () {
    gtmDataLayer.activateOptimize()
  }

  isPrepaidGratuitiesAvailable () {
    return this.quoteCriteria.departure.prices.prepaidGratuities
  }

  isTravelProtectionAvailable () {
    return this.travelProtectionPlans.anyWithTravelProtection()
  }
  toggleSpecialRequirements () {
    this.specialRequirementsOpened = !this.specialRequirementsOpened
  }

  toggleDiscountsAndOffers () {
    this.discountsAndOffersOpened = !this.discountsAndOffersOpened
  }

  isFrequentTravellerDiscountAvailable () {
    return this.features.showFTD
      && (this.departure.prices.adjustments.discounts.availableFrequentTravellerDiscount
        || this.departure.prices.adjustments.discounts.frequentTravellerDiscounts.length > 0)
  }

  isPastGuestBenefitAvailable () {
    return this.features.showPastGuestBenefit
      && !this.departure.prices.adjustments.discounts.availableFrequentTravellerDiscount
      && this.departure.startDate.differenceInDays(this.departure.endDate) >= 6
  }

  submit () {
    this.passengersBooking.submit({additionalValidation: () => this.validate()})
  }

  renderErrorMessage () {
    if( this.errorMessage){
      return <div class='message-info -strong'>{this.errorMessage}</div>
    }
  }

  renderInlineOffer () {
    const breakdown = this.priceBreakdown()
    const inlineOffer = breakdown.discounts.promotions.applied.find(p => p.inlineOffer)
    return inlineOffer
      ? <div class='message-success -strong inline-offer-added'>
        <h2>Online Special Offer Added!</h2>
        <p>Book online today for an extra {formatPrice({currency: breakdown.currency, amount: inlineOffer.amount})} off your booking</p>
      </div>
      : undefined
  }

  render () {
    this.showValidationErrors && this.validate()

    return <div data-test={'passenger-tab'} class='passenger-details'>
      { this.renderFillWithFakeData() }
      { this.renderInlineOffer() }
      { this.passengersBooking.renderFullNameWarning() }
      <form onsubmit={(ev) => { ev.preventDefault(); return this.submit() }}
        novalidate focusable-form name='passengerDetailsForm' id='passengerDetailsForm' class='u-clearfix'>
        { this.renderChildAgeError() }
        { this.serverErrorsPanel }
        { this.renderErrorMessage() }
        { this.scrollToName ? scroller.renderScroller() : null }
        { this.passengersBooking.passengersDetails }
        { this.renderExtrasSection() }
        { this.specialRequirementsSection }
      </form>
    </div>
  }

  createSection (sectionComponents, sectionAttributes) {
    return new PassengersTabSection({sectionComponents, sectionAttributes})
  }

  displayOnDesktop () {
    return window.innerWidth >= breakpoints.desktop.startWidth
  }

  createExtrasSection () {
    const sectionsComponents = []

    const opened = this.quoteCriteria.passengers.some(passenger => passenger.travelProtection && !passenger.travelProtectionPlan.isMandatory)
      || this.quoteCriteria.passengers.some(passenger => passenger.frequentTravellerDiscount.selected)
      || this.quoteCriteria.passengers.some(passenger => passenger.pastGuestBenefit.selected)
      || this.frequentTravellerDiscount.showValidationErrors
      || this.config.features.showTravelProtectionSection

    const ftdDisplayName = () => {
      if(this.isFrequentTravellerDiscountAvailable()) {
        return this.config.localisedWords.ftd
      }
      if(this.isPastGuestBenefitAvailable()) {
        return 'Past Guest Benefit'
      }
      return 'extra items'
    }
    let subtitle = `Add${this.config.styleCode === 'CH' ? ' 5% ' : ''} ${ftdDisplayName()} or ${this.config.localisedWords.insurance} to your trip`
    subtitle = this.config.insurance.isHidden ? subtitle.replace(`or ${this.config.localisedWords.insurance}`, ''): subtitle

    const sectionAttributes = {
      message: this.config.discountsOffersSection.showSubtitle ? subtitle : undefined,
      opened,
      sectionName: 'discounts-offers',
      title: this.config.discountsOffersSection.title,
    }
    return this.createSection(sectionsComponents, sectionAttributes)
  }

  createSpecialRequirementsSection () {
    const sectionComponents = []
    this.specialRequirements && sectionComponents.push(this.specialRequirements)

    const opened = this.quoteCriteria.passengers.some(passenger => passenger.specialRequirements)
      || this.config.features.showSpecialRequirementsSection

    const sectionAttributes = {
      opened,
      sectionName: 'special-requirements',
      title: 'Special Requirements',
    }
    return this.createSection(sectionComponents, sectionAttributes)
  }

  validate () {
    const result = {valid: true}
    if (this.isFrequentTravellerDiscountAvailable()) {
      const ftdValidationResult = this.frequentTravellerDiscount.validate()
      result.valid = ftdValidationResult.valid
      if (!ftdValidationResult.valid) {
        this.discountsAndExtrasSection.opened = true
        return result
      }
    } else if (this.isPastGuestBenefitAvailable()) {
      const pgbValidationResult = this.pastGuestBenefit.validate()
      result.valid = pgbValidationResult.valid
      if (!pgbValidationResult.valid) {
        this.discountsAndExtrasSection.opened = true
        return result
      }
    }

    const specialRequirementsValidation = this.specialRequirements.validate()
    if (!specialRequirementsValidation.valid) {
      result.valid = specialRequirementsValidation.valid
      this.specialRequirementsSection.opened = true
      scrollToAndFocus('.special-requirements-section', {skipFocus: true})
    }

    return result
  }

  renderChildAgeError () {
    if (this.passengersBooking.childAgeError) {
      return scroller.scrollIntoView(
        <div class='message-error -strong'>
          <h2>Errors</h2>
          <ul class='u-unstyled'>
            <li>{this.passengersBooking.childAgeError}</li>
          </ul>
        </div>
      )
    }
  }

  renderFillWithFakeData () {
    if (this.config.features.devLinks) {
      return <a href='#'
        onclick={(e) => {this.passengersBooking.passengersDetails.fillInWithFakeData(); e.preventDefault()}}
      >
        Fill in with fake data
      </a>
    }
  }

  renderExtrasSection () {
    const sectionComponents = []
    this.isFrequentTravellerDiscountAvailable() && sectionComponents.push(this.frequentTravellerDiscount)
      || (this.isPastGuestBenefitAvailable() && sectionComponents.push(this.pastGuestBenefit))
    this.isTravelProtectionAvailable() && sectionComponents.push(this.travelProtectionPlans)
    this.isPrepaidGratuitiesAvailable() && sectionComponents.push(this.prepaidGratuities)
    !this.displayOnDesktop() && sectionComponents.push(this.discountCode.renderDiscountBox({modal: false}))

    if (sectionComponents.length) {
      this.discountsAndExtrasSection.sectionComponents = sectionComponents
      return this.discountsAndExtrasSection
    }
  }
}
